import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Logo from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function googleLogin() {
  const redirectUrl = window.location.origin + "/";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export function AuthShell({ title, subtitle, children }) {
  return (
    <div className="min-h-screen bg-mesh flex flex-col items-center justify-center px-4 py-10">
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-6"><Logo size={64} /></div>
        <div className="glass rounded-3xl p-8 fade-up">
          <h1 className="font-head text-3xl font-extrabold text-center tracking-tight">{title}</h1>
          <p className="text-sm text-muted-foreground text-center mt-2 mb-7">{subtitle}</p>
          {children}
        </div>
        <p className="text-center text-xs text-muted-foreground mt-6">SINGVI KARAOKE — Sing. Record. Share.</p>
      </div>
    </div>
  );
}

export function GoogleButton() {
  return (
    <button onClick={googleLogin} data-testid="google-login-btn"
      className="w-full flex items-center justify-center gap-3 py-3 rounded-full glass hover:bg-white/10 transition-colors font-semibold text-sm">
      <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" className="w-5 h-5" alt="" />
      Continue with Google
    </button>
  );
}

export default function Login() {
  const { setUser } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data.user);
      toast.success(`Welcome back, ${data.user.display_name}!`);
      nav(loc.state?.from || (data.user.onboarded ? "/" : "/onboarding"), { replace: true });
    } catch (err) {
      toast.error(apiErr(err));
    } finally { setBusy(false); }
  };

  return (
    <AuthShell title="Welcome back" subtitle="Log in to keep singing">
      <form onSubmit={submit} className="space-y-4">
        <div>
          <Label className="text-xs">Email</Label>
          <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
            data-testid="login-email" className="mt-1.5 bg-input border-white/10" placeholder="you@example.com" />
        </div>
        <div>
          <Label className="text-xs">Password</Label>
          <Input type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
            data-testid="login-password" className="mt-1.5 bg-input border-white/10" placeholder="••••••••" />
        </div>
        <Button type="submit" disabled={busy} data-testid="login-submit"
          className="w-full rounded-full bg-neon-pink hover:bg-neon-pink/90 glow-pink font-bold py-6">
          {busy ? "Logging in..." : "Log in"}
        </Button>
      </form>
      <div className="flex items-center gap-3 my-5"><div className="flex-1 h-px bg-white/10" /><span className="text-xs text-muted-foreground">or</span><div className="flex-1 h-px bg-white/10" /></div>
      <GoogleButton />
      <p className="text-center text-sm text-muted-foreground mt-6">
        New here? <Link to="/register" className="text-neon-cyan font-semibold" data-testid="to-register">Join Free</Link>
      </p>
    </AuthShell>
  );
}
